import { reducerWithInitialState } from 'typescript-fsa-reducers/dist'

import * as actions from './actions'
import { PlayerStatus, PlaylistItem } from './types'
import { getPlaylistId } from './functions'
import { cloneMap } from '../root/functions'

export interface StoreState {
	readonly playlist: Map<string, PlaylistItem>
	readonly currentPlaylistId: string
}

const INITIAL_STATE: StoreState = {
	playlist: new Map(),
	currentPlaylistId: getPlaylistId(0, 0, undefined, 0),
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	.case(
		actions.loadAudioPreview,
		(state, payload): StoreState => {
			const { container, audio, data } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audio.id!, container.id, container.type, audio.file?.id!)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (!currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					playerStatus: PlayerStatus.Loading,
					duration: Infinity,
					currentTime: 0,
					audioTrack: {
						audio,
						coverImages: data?.coverImages,
						startTimes: data?.startTimes,
						fullScreenEnabled: data?.fullScreenEnabled,
						slideshowEnabled: data?.slideshowEnabled,
					},
					container: {
						type: container.type,
						id: container.id,
						title: container.title,
						collectionId: data?.collectionId,
						screen: data?.screen!,
					},
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
			}
		},
	)

	.case(
		actions.loadAudioError,
		(state, payload): StoreState => {
			const { audioId, error, container, fileId } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audioId, container.id, container.type, fileId)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					...currentAudioTrack,
					playerStatus: PlayerStatus.Error,
					audioTrack: {
						...currentAudioTrack.audioTrack,
						error,
					},
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
			}
		},
	)

	.case(
		actions.setCurrentTime,
		(state, payload): StoreState => {
			const { audioId, time, container, fileId } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audioId, container.id, container.type, fileId)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					...currentAudioTrack,
					currentTime: time,
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
				currentPlaylistId: playlistId,
			}
		},
	)

	.case(
		actions.audioLoaded,
		(state, payload): StoreState => {
			const { audioId, duration, container, fileId } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audioId, container.id, container.type, fileId)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					...currentAudioTrack,
					duration,
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
			}
		},
	)

	.case(
		actions.setPlayerStatus,
		(state, payload): StoreState => {
			const { audioId, status, container, fileId } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audioId, container.id, container.type, fileId)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					...currentAudioTrack,
					playerStatus: status,
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
				currentPlaylistId: playlistId,
			}
		},
	)

	.case(
		actions.setAudioEnded,
		(state, payload): StoreState => {
			const { audioId, container, fileId } = payload

			const updatedPlaylist = cloneMap(state.playlist) as Map<string, PlaylistItem>

			const playlistId = getPlaylistId(audioId, container.id, container.type, fileId)

			const currentAudioTrack: PlaylistItem | undefined = updatedPlaylist.get(playlistId)

			if (currentAudioTrack) {
				updatedPlaylist.set(playlistId, {
					...currentAudioTrack,
					playerStatus: PlayerStatus.Ended,
					currentTime: 0,
				})
			}

			return {
				...state,
				playlist: updatedPlaylist,
			}
		},
	)
